import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { ComponentsModule } from '@app/shared/components/components.module';
import { CommonModule, NgIf } from '@angular/common';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { SharedModule } from '@app/shared/shared.module';
import { AbstractControl, FormControl } from '@angular/forms';

@Component({
  selector: 'file-upload',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './file-upload.component.html',
  imports: [ComponentsModule, CommonModule, MatButton, MatIcon, MatIconButton, NgIf, SharedModule],
})
export class FileUploadComponent implements OnInit {
  @Input() fileFormControl: AbstractControl | null = null;
  @Input() buttonLabel: string = 'Upload file';
  @Input() buttonType: 'accent' | 'primary' = 'accent';
  // When true: the native file input accepts several files, one chip renders per selected
  // file with a per-index remove, `uploadedFileEvent` emits the whole `File[]`, and preview
  // (single-file only) is never emitted. When false (default), every existing consumer
  // (rating-tool, this drawer, claims add-credit-note) keeps today's exact single-file contract.
  @Input() multiple: boolean = false;
  @Output() uploadedFileEvent: EventEmitter<any> = new EventEmitter();
  @Output() uploadedFilePreviewEvent: EventEmitter<any> = new EventEmitter();

  // Backing state for both modes. `uploadedFile` below is a getter kept for the single-file
  // case so existing template bindings (`uploadedFile && uploadedFile.name`) keep working.
  public uploadedFiles: File[] = [];

  constructor() {}

  ngOnInit() {}

  public get uploadedFile(): File | undefined {
    return this.uploadedFiles[0];
  }

  public fileControl(): FormControl {
    return this.fileFormControl as FormControl;
  }

  public onFileChanged(event: any) {
    const files: FileList | undefined = event.target && event.target.files;
    if (!files || !files.length) {
      return;
    }

    if (this.multiple) {
      this.uploadedFiles = Array.from(files);
      this.uploadedFileEvent.emit(this.uploadedFiles);
      return;
    }

    const file = files[0];
    if (file) {
      this.uploadedFiles = [file];
      this.setupPreviewLink(file);
      this.uploadedFileEvent.emit(this.uploadedFile);
    }
  }

  public removeFile(index: number = 0) {
    if (this.multiple) {
      this.uploadedFiles = this.uploadedFiles.filter((_, fileIndex) => fileIndex !== index);
      // A native `<input type="file">` value can only ever be programmatically set back to
      // empty — assigning it the real File[] throws `InvalidStateError`. Only clear it (once no
      // files remain); otherwise leave its stale value alone, same as the single-file case below.
      if (!this.uploadedFiles.length) {
        this.fileFormControl?.patchValue(undefined);
      }
      this.uploadedFileEvent.emit(this.uploadedFiles);
      return;
    }

    this.uploadedFiles = [];
    this.fileFormControl.patchValue(undefined);
    this.uploadedFileEvent.emit(this.uploadedFile);
  }

  // Lets a parent replace the displayed selection without a user gesture (the Uber drawer prunes
  // already-accepted files after a partial rejection so a resubmit cannot re-upload them). Same
  // rules as `removeFile`: the bound control is only ever cleared, never set (see the comment
  // there), and nothing is emitted because the parent already holds the new list.
  public setFiles(files: File[]): void {
    this.uploadedFiles = [...files];
    if (!this.uploadedFiles.length) {
      this.fileFormControl?.patchValue(undefined);
    }
  }

  private setupPreviewLink(file: any): void {
    const reader = new FileReader();
    const filePreviewEvent = this.uploadedFilePreviewEvent;
    reader.readAsDataURL(file);
    reader.onload = function (evt) {
      const dataSrc = evt.target.result;
      filePreviewEvent.emit(dataSrc);
    };
  }
}
